import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../context/AuthContext';
import { useLanguage } from '../context/LanguageContext';
import { useTheme } from '../context/ThemeContext';
import { Button } from '../components/ui/button';
import { 
  ArrowLeft, Users, CreditCard, TrendingUp, Calendar,
  DollarSign, UserPlus, Activity, Download, Search,
  ChevronRight, Eye, CheckCircle, XCircle, Clock,
  BarChart3, RefreshCw, Mail, Send, Loader2, UserCheck
} from 'lucide-react';
import { toast } from 'sonner';

import { BACKEND_URL } from '../lib/backend';

const API_URL = BACKEND_URL;

const authFetch = (url, options = {}) => {
  const token = localStorage.getItem('auth_token');
  return fetch(url, {
    credentials: 'include',
    ...options,
    headers: { ...(options.headers || {}), ...(token ? { Authorization: `Bearer ${token}` } : {}) }
  });
};

const METHOD_STYLES = {
  phone: { label: 'Phone OTP', color: '#287A60', bg: 'rgba(40,122,96,0.12)' },
  email: { label: 'Email', color: '#C84B32', bg: 'rgba(200,75,50,0.1)' },
  google: { label: 'Google', color: '#2F6FB0', bg: 'rgba(118,183,242,0.18)' },
};

const timeAgo = (iso) => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 60) return 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return new Date(iso).toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
};

const MiniBars = ({ daily, field, color, testId }) => {
  const max = Math.max(1, ...daily.map((d) => d[field]));
  return (
    <div className="flex items-end gap-2 h-28" data-testid={testId}>
      {daily.map((d) => (
        <div key={d.date} className="flex-1 flex flex-col items-center gap-1">
          <span className="text-[10px] text-[#6B6560] font-medium">{d[field]}</span>
          <div className="w-full rounded-t-md" style={{ height: `${Math.max(4, (d[field] / max) * 80)}px`, background: color }} />
          <span className="text-[10px] text-[#8A8279]">{new Date(d.date).toLocaleDateString('en-IN', { weekday: 'short' })}</span>
        </div>
      ))}
    </div>
  );
};

const StatTile = ({ icon: Icon, label, value, sub, color, testId }) => (
  <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/5" data-testid={testId}>
    <div className="flex items-center gap-2 mb-2">
      <Icon className="w-4 h-4" style={{ color }} />
      <span className="text-xs text-[#8A8279]">{label}</span>
    </div>
    <p className="text-2xl font-bold text-[#282622]">{value}</p>
    {sub && <p className="text-xs text-[#8A8279] mt-1">{sub}</p>}
  </div>
);

export const AdminDashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useLanguage();
  const { isDark } = useTheme();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [searchTerm, setSearchTerm] = useState('');
  const [isAdmin, setIsAdmin] = useState(false);
  const [sendingEmails, setSendingEmails] = useState(false);

  useEffect(() => {
    checkAdminAccess();
  }, []);

  const checkAdminAccess = async () => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/check`);
      if (response.ok) {
        const data = await response.json();
        setIsAdmin(data.is_admin);
        if (data.is_admin) {
          fetchDashboardData();
        }
      }
    } catch (err) {
      console.error('Error checking admin access:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsRes, usersRes, paymentsRes] = await Promise.all([
        authFetch(`${API_URL}/api/admin/stats`),
        authFetch(`${API_URL}/api/admin/users`),
        authFetch(`${API_URL}/api/admin/payments`)
      ]);

      if (statsRes.ok) setStats(await statsRes.json());
      if (usersRes.ok) setUsers(await usersRes.json());
      if (paymentsRes.ok) setPayments(await paymentsRes.json());
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
    } finally {
      setLoading(false);
    }
  };

  const exportData = async (type) => {
    try {
      const response = await authFetch(`${API_URL}/api/admin/export/${type}`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `${type}_export_${new Date().toISOString().split('T')[0]}.csv`;
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        a.remove();
      }
    } catch (err) {
      console.error('Error exporting data:', err);
    }
  };

  const sendDailyHoroscopeEmails = async () => {
    setSendingEmails(true);
    try {
      const response = await authFetch(`${API_URL}/api/horoscope/send-daily-to-all`, { method: 'POST' });
      const data = await response.json();
      if (response.ok) {
        toast.success(`Daily horoscopes sent to ${data.sent} users!`);
      } else {
        toast.error(data.detail || 'Failed to send emails');
      }
    } catch (err) {
      toast.error('Error sending daily horoscope emails');
      console.error('Error:', err);
    } finally {
      setSendingEmails(false);
    }
  };

  const filteredUsers = users.filter(u => 
    u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.mobile?.includes(searchTerm)
  );

  const filteredPayments = payments.filter(p =>
    p.user_email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    p.order_id?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FFF9EF] flex items-center justify-center">
        <div className="animate-spin w-8 h-8 border-2 border-[#C84B32] border-t-transparent rounded-full" />
      </div>
    );
  }

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#FFF9EF] flex items-center justify-center text-[#282622]">
        <div className="text-center">
          <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold mb-2">Access Denied</h1>
          <p className="text-[#6B6560] mb-6">You don't have admin privileges.</p>
          <Button onClick={() => navigate('/dashboard')} className="cosmic-btn">
            Go to Dashboard
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FFF9EF] text-[#282622]">
      {/* Header */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-[#FFF9EF] border-b border-[#282622]/10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-center gap-4">
              <button 
                onClick={() => navigate('/dashboard')}
                className="p-2 rounded-lg hover:bg-[#FCE9D9]/60 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-[#6B6560]" />
              </button>
              <div>
                <h1 className="font-['Fraunces'] text-xl text-[#282622]">Admin Dashboard</h1>
                <p className="text-[#8A8279] text-xs">Manage users, payments & analytics</p>
              </div>
            </div>
            <Button 
              onClick={fetchDashboardData}
              variant="outline"
              size="sm"
              className="border-[#C84B32]/30 text-[#C84B32]"
            >
              <RefreshCw className="w-4 h-4 mr-2" />
              Refresh
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="cosmic-card p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <Users className="w-8 h-8 text-[#C84B32]" />
              <span className="text-xs text-green-600 bg-green-400/10 px-2 py-1 rounded-full">
                +{stats?.new_users_today || 0} today
              </span>
            </div>
            <p className="text-[#8A8279] text-sm">Total Users</p>
            <p className="text-3xl font-bold text-[#282622]">{stats?.total_users || 0}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="cosmic-card p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <CreditCard className="w-8 h-8 text-[#C84B32]" />
              <span className="text-xs text-[#C84B32] bg-[#C84B32]/10 px-2 py-1 rounded-full">
                {stats?.paid_users || 0} paid
              </span>
            </div>
            <p className="text-[#8A8279] text-sm">Active Subscriptions</p>
            <p className="text-3xl font-bold text-[#282622]">{stats?.active_subscriptions || 0}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="cosmic-card p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <DollarSign className="w-8 h-8 text-[#C84B32]" />
              <span className="text-xs text-green-600 bg-green-400/10 px-2 py-1 rounded-full">
                +₹{stats?.revenue_today || 0} today
              </span>
            </div>
            <p className="text-[#8A8279] text-sm">Total Revenue</p>
            <p className="text-3xl font-bold text-[#282622]">₹{stats?.total_revenue || 0}</p>
          </motion.div>

          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3 }}
            className="cosmic-card p-5"
          >
            <div className="flex items-center justify-between mb-3">
              <Activity className="w-8 h-8 text-[#C84B32]" />
            </div>
            <p className="text-[#8A8279] text-sm">Total Profiles Created</p>
            <p className="text-3xl font-bold text-[#282622]">{stats?.total_profiles || 0}</p>
          </motion.div>
        </div>

        {/* Email Marketing Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="cosmic-card p-6 mb-8"
        >
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-12 h-12 shrink-0 rounded-xl bg-gradient-to-br from-[#C84B32]/20 to-[#F5B942]/20 flex items-center justify-center">
                <Mail className="w-6 h-6 text-[#C84B32]" />
              </div>
              <div className="min-w-0">
                <h3 className="font-['Fraunces'] text-lg text-[#282622]">Daily Horoscope Emails</h3>
                <p className="text-[#8A8279] text-sm">Send personalized daily horoscope to all users</p>
              </div>
            </div>
            <Button
              onClick={sendDailyHoroscopeEmails}
              disabled={sendingEmails}
              className="bg-gradient-to-r from-[#C84B32] to-[#E8913A] hover:from-[#A63D28] hover:to-[#C84B32] text-white w-full sm:w-auto"
              data-testid="send-daily-horoscope-btn"
            >
              {sendingEmails ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Daily Horoscope to All
                </>
              )}
            </Button>
          </div>
          <p className="text-[#8A8279] text-xs mt-4">
            This will send a personalized horoscope email based on each user's rashi (determined from their profile DOB) with a CTA to check full reading in the app.
          </p>
        </motion.div>

        {/* Traffic Analytics Section */}
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5 }}
          className="cosmic-card p-6 mb-8"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-blue-500/20 to-cyan-500/20 flex items-center justify-center">
              <BarChart3 className="w-6 h-6 text-blue-600" />
            </div>
            <div>
              <h3 className="font-['Fraunces'] text-lg text-[#282622]">Traffic Analytics</h3>
              <p className="text-[#8A8279] text-sm">Who visited, who logged in, and how</p>
            </div>
          </div>
          
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <StatTile icon={Eye} label="Visits Today" value={stats?.traffic?.visits_today || 0} sub={`${stats?.traffic?.unique_visitors_today || 0} unique visitors`} color="#2F6FB0" testId="admin-visits-today" />
            <StatTile icon={Activity} label="Visits (7 days)" value={stats?.traffic?.visits_this_week || 0} sub={`${stats?.traffic?.unique_visitors_week || 0} unique • ${stats?.traffic?.total_visits || 0} all-time`} color="#287A60" testId="admin-visits-week" />
            <StatTile icon={UserCheck} label="Logged-in Users Today" value={stats?.traffic?.unique_logged_in_today || 0} sub={`${stats?.traffic?.logins_today || 0} logins today`} color="#C84B32" testId="admin-logged-in-today" />
            <StatTile icon={TrendingUp} label="Logged-in Users (7 days)" value={stats?.traffic?.unique_logged_in_week || 0} sub={`${stats?.traffic?.logins_this_week || 0} logins • ${stats?.traffic?.total_logins || 0} all-time`} color="#B7791F" testId="admin-logged-in-week" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mt-6">
            <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/5">
              <p className="text-sm font-semibold text-[#282622] mb-3">Logins by method (7 days)</p>
              <div className="space-y-3" data-testid="admin-logins-by-method">
                {Object.entries(METHOD_STYLES).map(([key, m]) => {
                  const count = stats?.traffic?.logins_by_method?.[key] || 0;
                  const total = Math.max(1, Object.values(stats?.traffic?.logins_by_method || {}).reduce((a, b) => a + b, 0));
                  return (
                    <div key={key}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[#4A4640] font-medium">{m.label}</span>
                        <span className="text-[#282622] font-semibold" data-testid={`admin-method-${key}`}>{count}</span>
                      </div>
                      <div className="h-2 rounded-full bg-[#FCE9D9]/70 overflow-hidden">
                        <div className="h-full rounded-full" style={{ width: `${(count / total) * 100}%`, background: m.color }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/5">
              <p className="text-sm font-semibold text-[#282622] mb-3">New signups (last 7 days)</p>
              <MiniBars daily={stats?.daily || []} field="signups" color="linear-gradient(180deg,#F5B942,#C84B32)" testId="admin-signups-chart" />
            </div>
            <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/5">
              <p className="text-sm font-semibold text-[#282622] mb-3">Visits (last 7 days)</p>
              <MiniBars daily={stats?.daily || []} field="visits" color="linear-gradient(180deg,#76B7F2,#2F6FB0)" testId="admin-visits-chart" />
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-6">
            <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/5">
              <p className="text-sm font-semibold text-[#282622] mb-3">Recent logins</p>
              <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="admin-recent-logins">
                {(stats?.recent_logins || []).length === 0 && <p className="text-xs text-[#8A8279]">No logins recorded yet.</p>}
                {(stats?.recent_logins || []).map((l, i) => {
                  const m = METHOD_STYLES[l.method] || METHOD_STYLES.email;
                  return (
                    <div key={i} className="flex items-center justify-between gap-3 bg-white rounded-lg px-3 py-2" data-testid={`admin-recent-login-${i}`}>
                      <div className="min-w-0">
                        <p className="text-sm text-[#282622] font-medium truncate">{l.name}</p>
                        <p className="text-xs text-[#8A8279] truncate">{l.mobile || l.email || l.user_id}</p>
                      </div>
                      <div className="flex items-center gap-2 shrink-0">
                        <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full" style={{ color: m.color, background: m.bg }}>{m.label}</span>
                        <span className="text-xs text-[#8A8279] w-16 text-right">{timeAgo(l.timestamp)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
            <div className="bg-[#FFF9EF] rounded-xl p-4 border border-[#282622]/5">
              <p className="text-sm font-semibold text-[#282622] mb-3">Most visited pages (7 days)</p>
              <div className="space-y-2" data-testid="admin-top-pages">
                {(stats?.top_pages || []).length === 0 && <p className="text-xs text-[#8A8279]">No visits recorded yet.</p>}
                {(stats?.top_pages || []).map((p, i) => {
                  const max = Math.max(1, stats.top_pages[0].visits);
                  return (
                    <div key={p.page} className="relative bg-white rounded-lg px-3 py-2 overflow-hidden">
                      <div className="absolute inset-y-0 left-0 bg-[#FCE9D9]" style={{ width: `${(p.visits / max) * 100}%` }} />
                      <div className="relative flex justify-between text-sm">
                        <span className="text-[#282622] font-mono text-xs">{p.page}</span>
                        <span className="text-[#C84B32] font-semibold text-xs">{p.visits}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </motion.div>

        {/* Tabs */}
        <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
          {[
            { id: 'overview', label: 'Overview', icon: BarChart3 },
            { id: 'users', label: 'Users', icon: Users },
            { id: 'payments', label: 'Payments', icon: CreditCard }
          ].map(tab => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg whitespace-nowrap transition-all ${
                activeTab === tab.id 
                  ? 'bg-[#C84B32] text-white' 
                  : 'bg-white text-[#6B6560] hover:bg-[#FCE9D9]/60'
              }`}
            >
              <tab.icon className="w-4 h-4" />
              {tab.label}
            </button>
          ))}
        </div>

        {/* Search & Export */}
        {activeTab !== 'overview' && (
          <div className="flex flex-col sm:flex-row gap-4 mb-6">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8A8279]" />
              <input
                type="text"
                placeholder={`Search ${activeTab}...`}
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 bg-white border border-[#282622]/10 rounded-lg text-[#282622] placeholder-[#6B7280] focus:border-[#C84B32]/50 focus:outline-none"
              />
            </div>
            <Button
              onClick={() => exportData(activeTab)}
              className="bg-white border border-[#C84B32]/30 text-[#C84B32] hover:bg-[#C84B32]/10"
            >
              <Download className="w-4 h-4 mr-2" />
              Export CSV
            </Button>
          </div>
        )}

        {/* Content */}
        {activeTab === 'overview' && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Revenue Chart Placeholder */}
            <div className="cosmic-card p-6">
              <h3 className="font-['Fraunces'] text-lg text-[#282622] mb-4">Revenue Overview</h3>
              <div className="space-y-4">
                <div className="flex justify-between items-center">
                  <span className="text-[#6B6560]">This Month</span>
                  <span className="text-[#C84B32] font-bold">₹{stats?.revenue_this_month || 0}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="text-[#6B6560]">Last Month</span>
                  <span className="text-[#282622]">₹{stats?.revenue_last_month || 0}</span>
                </div>
                <div className="h-2 bg-white rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-gradient-to-r from-[#C84B32] to-[#F5B942] rounded-full"
                    style={{ width: `${Math.min((stats?.revenue_this_month || 0) / Math.max(stats?.revenue_last_month || 1, 1) * 100, 100)}%` }}
                  />
                </div>
              </div>
            </div>

            {/* Subscription Breakdown */}
            <div className="cosmic-card p-6">
              <h3 className="font-['Fraunces'] text-lg text-[#282622] mb-4">Subscription Tiers</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between p-3 bg-[#FFF9EF] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-gray-500" />
                    <span className="text-[#6B6560]">Free</span>
                  </div>
                  <span className="text-[#282622] font-medium">{stats?.tier_free || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#FFF9EF] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-[#C84B32]" />
                    <span className="text-[#6B6560]">Stellar (₹99)</span>
                  </div>
                  <span className="text-[#282622] font-medium">{stats?.tier_stellar || 0}</span>
                </div>
                <div className="flex items-center justify-between p-3 bg-[#FFF9EF] rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-3 rounded-full bg-[#C84B32]" />
                    <span className="text-[#6B6560]">Cosmic (₹199)</span>
                  </div>
                  <span className="text-[#282622] font-medium">{stats?.tier_cosmic || 0}</span>
                </div>
              </div>
            </div>

            {/* Recent Activity */}
            <div className="cosmic-card p-6 lg:col-span-2">
              <h3 className="font-['Fraunces'] text-lg text-[#282622] mb-4">Recent Signups</h3>
              <div className="space-y-3">
                {users.slice(0, 5).map((user, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-[#FFF9EF] rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center text-sm font-bold text-white">
                        {user.name?.[0] || user.email?.[0]?.toUpperCase() || '?'}
                      </div>
                      <div>
                        <p className="text-[#282622] text-sm">{user.name || 'Unknown'}</p>
                        <p className="text-[#8A8279] text-xs">{user.email || user.mobile}</p>
                      </div>
                    </div>
                    <div className="text-right">
                      <span className={`text-xs px-2 py-1 rounded-full ${
                        ['cosmic', 'premium'].includes(user.subscription_tier) ? 'bg-[#F5B942]/25 text-[#B7791F]' :
                        ['stellar', 'basic'].includes(user.subscription_tier) ? 'bg-[#C84B32]/20 text-[#C84B32]' :
                        'bg-gray-500/20 text-[#6B6560]'
                      }`}>
                        {user.subscription_tier || 'free'}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'users' && (
          <div className="cosmic-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#282622]/10">
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">User</th>
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">Subscription</th>
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">Profiles</th>
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">Joined</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredUsers.map((user, i) => (
                    <tr key={i} className="border-b border-[#282622]/10 hover:bg-[#FCE9D9]/60">
                      <td className="p-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#C84B32] to-[#F5B942] flex items-center justify-center text-sm font-bold text-white">
                            {user.name?.[0] || user.email?.[0]?.toUpperCase() || '?'}
                          </div>
                          <div>
                            <p className="text-[#282622] text-sm">{user.name || 'Unknown'}</p>
                            <p className="text-[#8A8279] text-xs">{user.email || user.mobile}</p>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          ['cosmic', 'premium'].includes(user.subscription_tier) ? 'bg-[#F5B942]/25 text-[#B7791F]' :
                          ['stellar', 'basic'].includes(user.subscription_tier) ? 'bg-[#C84B32]/20 text-[#C84B32]' :
                          'bg-gray-500/20 text-[#6B6560]'
                        }`}>
                          {user.subscription_tier || 'free'}
                        </span>
                      </td>
                      <td className="p-4 text-[#6B6560]">{user.profile_count || 0}</td>
                      <td className="p-4 text-[#8A8279] text-sm">
                        {new Date(user.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredUsers.length === 0 && (
                <div className="text-center py-12 text-[#8A8279]">
                  No users found
                </div>
              )}
            </div>
          </div>
        )}

        {activeTab === 'payments' && (
          <div className="cosmic-card overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#282622]/10">
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">Order ID</th>
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">User</th>
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">Amount</th>
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">Plan</th>
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">Status</th>
                    <th className="text-left p-4 text-[#8A8279] text-sm font-medium">Date</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredPayments.map((payment, i) => (
                    <tr key={i} className="border-b border-[#282622]/10 hover:bg-[#FCE9D9]/60">
                      <td className="p-4 text-[#6B6560] text-sm font-mono">{payment.order_id?.slice(0, 15)}...</td>
                      <td className="p-4 text-[#282622] text-sm">{payment.user_email}</td>
                      <td className="p-4 text-[#C84B32] font-medium">₹{payment.amount}</td>
                      <td className="p-4">
                        <span className={`text-xs px-2 py-1 rounded-full ${
                          ['cosmic', 'premium'].includes(payment.tier) ? 'bg-[#F5B942]/25 text-[#B7791F]' :
                          'bg-[#C84B32]/20 text-[#C84B32]'
                        }`}>
                          {payment.tier}
                        </span>
                      </td>
                      <td className="p-4">
                        <span className={`flex items-center gap-1 text-xs ${
                          payment.status === 'success' ? 'text-green-600' :
                          payment.status === 'pending' ? 'text-yellow-600' :
                          'text-red-600'
                        }`}>
                          {payment.status === 'success' ? <CheckCircle className="w-3 h-3" /> :
                           payment.status === 'pending' ? <Clock className="w-3 h-3" /> :
                           <XCircle className="w-3 h-3" />}
                          {payment.status}
                        </span>
                      </td>
                      <td className="p-4 text-[#8A8279] text-sm">
                        {new Date(payment.created_at).toLocaleDateString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
              {filteredPayments.length === 0 && (
                <div className="text-center py-12 text-[#8A8279]">
                  No payments found
                </div>
              )}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
